import React from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AdminRoute } from './domain/components/rbac/AdminRoute';
import { useAuth } from './domain/context/auth';
import { RBACProvider } from './domain/context/rbac';
import DashboardLayout from './presentation/components/layout/dashboard/app-shell';
import AdminLogsPage from './presentation/pages/admin-logs';
import { AdminUsersPage } from './presentation/pages/admin-users';
import { AddAdminUserPage } from './presentation/pages/admin-users/add';
import { EditAdminUserPage } from './presentation/pages/admin-users/edit';
import Login from './presentation/pages/auth/login';
import BannersPage from './presentation/pages/banners';
import BannerAddPage from './presentation/pages/banners/add';
import BannerDetailsPage from './presentation/pages/banners/detail';
import CriticalStandarsPage from './presentation/pages/critical-standars';
import CriticalStandardsAddPage from './presentation/pages/critical-standars/add';
import CriticalStandardsDetailsPage from './presentation/pages/critical-standars/detail';
import { DashboardPage } from './presentation/pages/dashboard';
import DeleteAccount from './presentation/pages/delete-account';
import GalleriesPage from './presentation/pages/galleries';
import GalleryAddPage from './presentation/pages/galleries/add';
import GalleryDetailsPage from './presentation/pages/galleries/detail';
import LinksPage from './presentation/pages/links';
import LinkAddPage from './presentation/pages/links/add';
import LinkDetailsPage from './presentation/pages/links/detail';
import NewslettersPage from './presentation/pages/newsletters';
import NewsletterAddPage from './presentation/pages/newsletters/add';
import NewsletterDetailsPage from './presentation/pages/newsletters/detail';
import PodcastsPage from './presentation/pages/podcasts';
import PodcastAddPage from './presentation/pages/podcasts/add';
import PodcastDetailsPage from './presentation/pages/podcasts/detail';
import PoliciesPage from './presentation/pages/policies';
import PoliciesPrivacityPage from './presentation/pages/policies-privacity';
import PolicyAddPage from './presentation/pages/policies/add';
import PolicyDetailsPage from './presentation/pages/policies/detail';
import ReleasesPage from './presentation/pages/releases';
import ReleaseAddPage from './presentation/pages/releases/add';
import ReleaseDetailsPage from './presentation/pages/releases/detail';
import ReportsPages from './presentation/pages/reports';
import ReportAddPage from './presentation/pages/reports/add';
import ReportDetailsPage from './presentation/pages/reports/detail';
import { RolesPage } from './presentation/pages/roles';
import { RoleAddPage } from './presentation/pages/roles/add';
import { EditRolePage } from './presentation/pages/roles/edit';
import SettingsPage from './presentation/pages/settings';
import ReportByEmployeePage from './presentation/pages/statistics/report-by-employee';
import ReportByFilterPage from './presentation/pages/statistics/report-by-filter';
import ReportUsersLoggedPage from './presentation/pages/statistics/report-users-logged';
import UsersPage from './presentation/pages/users';
import UsersSelfRegisteredPage from './presentation/pages/users/self-registered';
import UsersDomainAdminPage from './presentation/pages/users-domain-admin';
import UsersUpdatePage from './presentation/pages/users/users-update';
import UsersUploadPage from './presentation/pages/users/users-upload';
import CampaignsPage from './presentation/pages/campaigns';
import CampaignAddPage from './presentation/pages/campaigns/add';
import CampaignDetailPage from './presentation/pages/campaigns/detail';
import AudiencesPage from './presentation/pages/audiences';
import AudienceAddPage from './presentation/pages/audiences/add';
import AudienceMembersPage from './presentation/pages/audiences/members';
import VideosPage from './presentation/pages/videos';
import VideoAddPage from './presentation/pages/videos/add';
import VideoDetailsPage from './presentation/pages/videos/detail';

const App: React.FC = () => {
  const { user, isLoadingUser } = useAuth();

  if (isLoadingUser) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );
  }

  return (
    <Router>
      <ToastContainer />
      <Routes>
        <Route
          path="/"
          element={user ? <Navigate to="/dashboard/home" /> : <Login />}
        />
        <Route path="privacy-policy" element={<PoliciesPrivacityPage />} />
        <Route path="delete-account" element={<DeleteAccount />} />
        <Route
          path="/dashboard"
          element={
            <AdminRoute>
              <RBACProvider>
                <DashboardLayout />
              </RBACProvider>
            </AdminRoute>
          }
        >
          <Route path="home" element={<DashboardPage />} />
          <Route path="newsletters" element={<NewslettersPage />} />
          <Route path="newsletters/add" element={<NewsletterAddPage />} />
          <Route
            path="newsletters/details/:id"
            element={<NewsletterDetailsPage />}
          />
          <Route path="policies" element={<PoliciesPage />} />
          <Route path="policies/add" element={<PolicyAddPage />} />
          <Route path="policies/details/:id" element={<PolicyDetailsPage />} />
          <Route path="critical-standars" element={<CriticalStandarsPage />} />
          <Route
            path="critical-standars/add"
            element={<CriticalStandardsAddPage />}
          />
          <Route
            path="critical-standars/details/:id"
            element={<CriticalStandardsDetailsPage />}
          />
          <Route path="releases" element={<ReleasesPage />} />
          <Route path="releases/add" element={<ReleaseAddPage />} />
          <Route path="releases/details/:id" element={<ReleaseDetailsPage />} />
          <Route path="reports" element={<ReportsPages />} />
          <Route path="reports/add" element={<ReportAddPage />} />
          <Route path="reports/details/:id" element={<ReportDetailsPage />} />
          <Route path="podcasts" element={<PodcastsPage />} />
          <Route path="podcasts/add" element={<PodcastAddPage />} />
          <Route path="podcasts/details/:id" element={<PodcastDetailsPage />} />
          <Route path="videos" element={<VideosPage />} />
          <Route path="videos/add" element={<VideoAddPage />} />
          <Route path="videos/details/:id" element={<VideoDetailsPage />} />
          <Route path="links" element={<LinksPage />} />
          <Route path="links/add" element={<LinkAddPage />} />
          <Route path="links/details/:id" element={<LinkDetailsPage />} />
          <Route path="galleries" element={<GalleriesPage />} />
          <Route path="galleries/add" element={<GalleryAddPage />} />
          <Route
            path="galleries/details/:id"
            element={<GalleryDetailsPage />}
          />
          <Route path="banners" element={<BannersPage />} />
          <Route path="banners/add" element={<BannerAddPage />} />
          <Route path="banners/details/:id" element={<BannerDetailsPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="roles/add" element={<RoleAddPage />} />
          <Route path="roles/:roleId" element={<EditRolePage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="admin-users" element={<AdminUsersPage />} />
          <Route path="admin-users/add" element={<AddAdminUserPage />} />
          <Route path="admin-users/:userId" element={<EditAdminUserPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="users/upload" element={<UsersUploadPage />} />
          <Route path="users/update" element={<UsersUpdatePage />} />
          <Route
            path="users/self-registered"
            element={<UsersSelfRegisteredPage />}
          />
          <Route path="users-domain-admin" element={<UsersDomainAdminPage />} />
          <Route path="admin-logs" element={<AdminLogsPage />} />
          <Route
            path="statistics/report-by-filter"
            element={<ReportByFilterPage />}
          />
          <Route
            path="statistics/report-by-employee"
            element={<ReportByEmployeePage />}
          />
          <Route
            path="statistics/report-users-logged"
            element={<ReportUsersLoggedPage />}
          />
          <Route path="campaigns" element={<CampaignsPage />} />
          <Route path="campaigns/add" element={<CampaignAddPage />} />
          <Route path="campaigns/detail/:id" element={<CampaignDetailPage />} />
          <Route path="audiences" element={<AudiencesPage />} />
          <Route path="audiences/add" element={<AudienceAddPage />} />
          <Route
            path="audiences/members/:id"
            element={<AudienceMembersPage />}
          />
        </Route>
      </Routes>
    </Router>
  );
};

export default App;
